import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "@/lib/api";
import { PageHeader, StatusDot, hdrCell, ghostBtn as uiGhostBtn } from "@/components/admin-ui";
import { useToast } from "@/components/ui-prims";

type Run = {
  id?: number;
  source: string;
  mode?: string;
  status?: string;            // running | success | error
  started_at?: string | null;
  finished_at?: string | null;
  stage?: string | null;
  progress?: number | null;   // 0..100
  rows_in?: number | null;
  rows_upserted?: number | null;
  error?: string | null;
};

// /sync/status returns { sources: [Run, …], known: [string, …] } — `sources`
// is the latest run per source (one row each, may be empty for never-run
// jobs), and `known` is the scheduler's full source list including jobs
// that have never executed.
type StatusResponse = { sources?: Run[]; known?: string[] };

export function JobsPage() {
  // statuses keyed by source name for O(1) lookup at render time.
  const [statuses, setStatuses] = useState<Record<string, Run>>({});
  // knownSources is the union of `known` from the engine and any source
  // we've seen in `sources` — covers the case where the engine is older
  // than this UI and only emits one of the two.
  const [knownSources, setKnownSources] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [triggering, setTriggering] = useState<string | null>(null);
  const toast = useToast();
  // Track jobs the current admin kicked off so we can fire a
  // completion toast when their status flips out of "running".
  // Keyed by source; value is the run id we dispatched against.
  const watching = useRef<Map<string, number | null>>(new Map());

  const refresh = useCallback(async () => {
    try {
      const data = await api.get<StatusResponse>("/admin/dataengine/status");
      const runs = data.sources ?? [];
      const map: Record<string, Run> = {};
      for (const r of runs) {
        if (r.source) map[r.source] = r;
      }
      setStatuses(map);
      const all = new Set<string>(data.known ?? []);
      for (const s of Object.keys(map)) all.add(s);
      setKnownSources(Array.from(all).sort());
      setErr(null);

      // Fire a completion toast for any job we kicked off that's no
      // longer running. Avoids surprising staff who scroll away and
      // never see the row flip.
      for (const [src] of watching.current) {
        const cur = map[src];
        if (cur && cur.status && cur.status !== "running") {
          watching.current.delete(src);
          if (cur.status === "success") {
            toast(
              `${src} finished · ${fmtInt(cur.rows_upserted ?? null)} rows`,
              { tone: "success" },
            );
          } else if (cur.status === "skipped") {
            toast(`${src} skipped (another run held the lock)`, { tone: "info" });
          } else {
            toast(`${src} ${cur.status}${cur.error ? ` — ${cur.error}` : ""}`, { tone: "error" });
          }
        }
      }
    } catch (e: any) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  }, [toast]);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), 2000);
    return () => clearInterval(id);
  }, [refresh]);

  const runJob = async (source: string, mode: "incremental" | "backfill") => {
    if (triggering) return;
    setTriggering(source);
    try {
      await api.post("/admin/dataengine/run", { source, mode });
      watching.current.set(source, null);
      toast(`Queued ${mode} for ${source}`, { tone: "success" });
      await refresh();
    } catch (e: any) {
      const msg = e?.message ?? "run failed";
      setErr(msg);
      toast(`Couldn't queue ${source}: ${msg}`, { tone: "error" });
    } finally {
      setTriggering(null);
    }
  };

  // Render in the dataengine's source order when available; fall back to
  // alphabetical for any extras only present in statuses.
  const sources = knownSources;

  return (
    <>
      <PageHeader
        kicker="Data engine"
        title="Jobs"
        sub="Per-source ingest jobs. Manual runs are idempotent — incremental is safe to re-trigger, backfill re-ingests the full window."
        actions={
          <button onClick={() => void refresh()} style={uiGhostBtn} type="button">Refresh</button>
        }
      />

      {err && (
        <div style={{ margin: "0 26px 14px", background: "rgba(122,45,31,0.08)", color: "var(--neg)", padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>
          {err}
        </div>
      )}

      <div style={{
        margin: "0 26px",
        border: "1px solid var(--rule)", borderRadius: 10, background: "var(--surface)",
        overflow: "hidden",
      }}>
        <div style={{ ...rowGrid, ...hdrCell, padding: "10px 16px", borderBottom: "1px solid var(--rule)", background: "var(--surface-2)" }}>
          <div>Source</div>
          <div>Status</div>
          <div>Stage / progress</div>
          <div style={{ textAlign: "right" }}>Rows upserted</div>
          <div style={{ textAlign: "right" }}>Last finished</div>
          <div style={{ textAlign: "right" }}>Actions</div>
        </div>
        {sources.length === 0 && !err && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--ink-3)", fontStyle: "italic" }}>
            Loading…
          </div>
        )}
        {sources.map((s) => {
          // r may be undefined for known sources that have never run.
          const r: Run | undefined = statuses[s];
          const isRunning = r?.status === "running";
          return (
            <div
              key={s}
              className="ops-row"
              style={{ ...rowGrid, padding: "12px 16px", borderBottom: "1px solid var(--rule)", cursor: "default" }}
            >
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, fontWeight: 500, color: "var(--ink)" }}>{s}</div>
              <div><StatusDot status={r?.status ?? "—"} /></div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, color: "var(--ink-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r?.stage || "—"}
                </div>
                {typeof r?.progress === "number" && isRunning && (
                  <ProgressBar value={r.progress} />
                )}
              </div>
              <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12, color: r?.rows_upserted ? "var(--ink-2)" : "var(--ink-4)" }}>
                {fmtInt(r?.rows_upserted ?? null)}
              </div>
              <div style={{ textAlign: "right", fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-3)" }}>
                {fmtTime(r?.finished_at ?? r?.started_at)}
              </div>
              <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                <button
                  onClick={() => void runJob(s, "incremental")}
                  disabled={triggering === s || isRunning}
                  style={smallBtn}
                  title="Incremental run"
                >Inc</button>
                <button
                  onClick={() => void runJob(s, "backfill")}
                  disabled={triggering === s || isRunning}
                  style={smallBtn}
                  title="Full backfill"
                >Backfill</button>
              </div>
            </div>
          );
        })}
      </div>

      <p style={{ margin: "14px 26px 26px", fontSize: 11.5, color: "var(--ink-3)", lineHeight: 1.5 }}>
        Status polls every 2s. Manual runs are idempotent: the dataengine uses
        source_hash uniqueness so re-running an incremental job is safe.
        Backfill re-ingests the full window — use sparingly.
      </p>
    </>
  );
}

function ProgressBar({ value }: { value: number }) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <div style={{ marginTop: 4, height: 4, borderRadius: 2, background: "var(--surface-3)", overflow: "hidden" }}>
      <div style={{ width: `${v}%`, height: "100%", background: "var(--accent)", transition: "width 200ms ease" }} />
    </div>
  );
}

function fmtInt(n: number | null): string {
  if (n == null) return "—";
  return n.toLocaleString("en-SG");
}

function fmtTime(s?: string | null): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("en-SG", { dateStyle: "short", timeStyle: "short" });
}

const smallBtn: React.CSSProperties = { padding: "4px 9px", borderRadius: 5, border: "1px solid var(--rule)", background: "var(--surface)", color: "var(--ink-2)", cursor: "pointer", fontSize: 11.5 };
const rowGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 100px 1.4fr 120px 140px 140px", gap: 14, alignItems: "center" };
